import { computed, effect, EffectRef, inject, Injectable, isDevMode, Signal, untracked } from '@angular/core';
import { MapViewerInitOptions, MapViewerService, Viewer3dService as Viewer3dSdkService } from '@3ddv/ngx-dvm-internal';
import { MapViewerFlagsAPI, MapViewerLoadMapOptions, MapViewerLoadObject, MapViewerStylesLevel, MapViewerStylesState, Viewer3dLoadObject } from '@3ddv/dvm-internal';
import { AppViews } from '../models/enums/dvm/DvmViews.enum';
import { Configuration } from '../models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '../configuration/configuration';
import { catchError, first, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { MINIMAP, VIEWER, VIEWER3D } from '@dvm.providers';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from './ui.service';
import { DvmTemplates } from '@models/enums/dvm/DvmTemplates.enum';
import { ViewerService } from './dvm/viewer.service';
import { Viewer3dService } from './dvm/viewer3d.service';
import { ViewDeterminationService } from './dvm/view-determination.service';
import { VenueConfigurationsService } from './dvm/venue-configurations.service';


@Injectable({
  providedIn: 'root'
})
export class DvmService {

  // CONFIGURATION
  private readonly config:                Signal<Configuration>            = inject(APP_CONFIG);
  private readonly dvmConfig:             Signal<Configuration['Dvm']>     = computed((): Configuration['Dvm'] => this.config().Dvm);
  private readonly uiconfig:              Signal<UiConfiguration>          = inject(UiService).uiConfig;


  // COMPUTED CONFIGURATION
  private readonly seatLevelEnabled:      Signal<boolean>                  = computed((): boolean => this.uiconfig().SeatLevelDisplay.enabled);


  // SUB-SERVICES (FACADE DELEGATION)
  private readonly viewer2d:              ViewerService                    = inject(ViewerService);
  private readonly viewer3dService:       Viewer3dService                  = inject(Viewer3dService);
  private readonly viewDetermination:     ViewDeterminationService         = inject(ViewDeterminationService);
  private readonly venueConfigs:          VenueConfigurationsService       = inject(VenueConfigurationsService);


  // DVM SERVICES (delegated to sub-services, exposed for backward compatibility)
  public readonly viewer:                 MapViewerService                 = inject(VIEWER);
  public readonly minimap:                MapViewerService                 = inject(MINIMAP);
  public readonly viewer3d:               Viewer3dSdkService               = inject(VIEWER3D);


  // MINIMAP CONFIGURATION
  private minimapInitOptions:              MapViewerInitOptions            = {};
  public  minimapLoadOptions:              MapViewerLoadMapOptions         = {venue_id: '', map_id: ''};


  // STATE (delegated to sub-services, exposed for backward compatibility)
  public  readonly is3d                   = this.viewer3dService.is3d;
  public  readonly isFullscreen           = this.viewer3dService.isFullscreen;
  public  readonly isMultilevel:          Signal<boolean>                  = computed((): boolean => this.dvmConfig().viewer.isMultilevel ?? false);
  public  readonly view:                  Signal<AppViews|null>           = this.viewer2d.view;


  // Ultimo `forceMainView` visto por `configChange`. `null` en la primera pasada.
  private previousForceMainView:          boolean|null                    = null;


  // EFFECTS
  public readonly configChange:            EffectRef                       = effect((): void => {

    // Config efectiva, no `Dvm.viewer` crudo: es a la vez el disparador reactivo de la conmutacion
    // y la base de comparacion. Con la config cruda, cualquier otro cambio en `Dvm` compararia los
    // ids de la principal contra el mapa activo y devolveria al usuario a la principal.
    const config:   Configuration['Dvm']['viewer'] = this.venueConfigs.viewerConfig(),
          current:  MapViewerLoadMapOptions        = this.viewer2d.viewerLoadOptions,  // Conseguimos la configuración del mapa actual,
          importMode: boolean                      = untracked(this.viewer2d.importMode)

    // Antes de los early return: si la primera pasada que llega al bloque VIEWS fuese ya la del
    // cambio, el valor anterior seria `null` y desactivar el flag no recalcularia. Registrar en una
    // pasada que corta no pierde nada: la carga del mapa fija la vista por su cuenta.
    const forceMainView:       boolean = config?.forceMainView ?? false,
          forceMainViewToggled: boolean = this.previousForceMainView !== null && this.previousForceMainView !== forceMainView;

    this.previousForceMainView = forceMainView;

    untracked((): void => {

      // VIEWERS: Si los viewers no están inicializados o la configuración es nula, no hacemos nada.
      if( (!this.viewer.isInitialized() || !this.viewer3d.isInitialized()) || !config ) return;


      // CONFIG: Si la configuración del DVM ha cambiado, actualizamos los viewers.
      if( (config.venueId !== current.venue_id) || (config.mapId !== current.map_id ) ) {
        // Si la vista es 3D, cerramos el viewer 3D.
        if(this.is3d()) this.close3dView();
 
        // En importacion el dialogo ya inicializa los viewers, asi que aqui no hacemos nada.
        // Como el mapa no se recarga, ahi el selector de venue configurations va deshabilitado.
        if(!importMode) this.initViewers();
        
        return;

      }

      // Skip 2D map-dependent operations when no map is configured (e.g. 3D-only sites)
      if (!this.viewer2d.viewerLoadOptions.map_id) return;

      //SEAT LEVEL: Check if seatLevel has changes and set layers viewer
      const seatLevel: boolean = this.seatLevelEnabled();
      if (seatLevel !== null) this.setLayers(!seatLevel, seatLevel ? 1 : 0);
      

      //AVAILABILITY: check if custom availaibility or random availability has changed
      const {enabled, data}          = this.viewer2d.customAvailability(),
            {enabled: randomEnabled} = this.viewer2d.randomAvailability();
    
      if (enabled && data) this.viewer2d.handleCustomAvailaibilityData();
      if (randomEnabled)   this.viewer2d.handleRandomAvailablity();

      // VIEWS
      // `forceMainViewToggled`: al desactivar el flag ninguna otra condicion recalcula y la vista se
      // quedaria en Main.
      const {is3d, isMultilevel} = this.dvmConfig().viewer;

      if(is3d || isMultilevel || forceMainView || forceMainViewToggled) this.setCurrentView();

      // MINIMAP: config efectiva. Dos configuraciones pueden compartir `minimapId` con distinto
      // venue; sin la comparacion de venue, el minimapa anterior se quedaria cargado en silencio.
      const minimapConfig:  Configuration['Dvm']['minimap'] = this.venueConfigs.minimapConfig(),
            minimapCurrent: MapViewerLoadMapOptions         = this.minimapLoadOptions;

      // Si el minimapa no está inicializado o la configuración del minimapa ha cambiado, lo inicializamos.
      if( minimapConfig.enabled && (!this.minimap.isInitialized()
        || minimapConfig.mapId !== minimapCurrent.map_id
        || config.venueId !== minimapCurrent.venue_id)) {

        this.initMinimap();

      }
      
      // STYLES: Update styles 
      if (config.styles && this.viewer.isInitialized()) {
        
        // Limpiamos estilos
        this.clearStyles();
 
        // Si es "default" o "new", llamamos a recargar el mapa para traer los estilos default
        if(Object.values(DvmTemplates).includes( config.styles.selected! as DvmTemplates)) {
          this.loadMap().subscribe({
            error: (error: unknown): void => console.error('Error reloading map styles', error)
          });
          return;
        }
 
        // Si no, pasamos la nueva template seleccionada a viewerStyles y aplicamos cambios sin recargar mapa
        this.viewer2d.setViewerInitStyles(config.styles);
        if(this.viewer2d.viewerStyles) this.viewer2d.setViewerStyles(this.viewer2d.viewerStyles);
      
      }

    })

  });


  // GETTERS
  /**
   * Devuelve el venue cargado en el viewer 2D.
   *
   * @returns Identificador del venue o `null` si no hay viewer inicializado.
   */
  public  get currentVenue():  string | null {
    return this.viewer2d.currentVenue;
  }

  /**
   * Devuelve el mapa cargado en el viewer 2D.
   *
   * @returns Identificador del mapa o `null` si no hay viewer inicializado.
   */
  public  get currentMap():    string | null {
    return this.viewer2d.currentMap;
  }

  /**
   * Devuelve la vista 3D cargada en el viewer 3D.
   *
   * @returns Identificador de la vista 3D o `null` si no hay viewer inicializado.
   */
  public  get currentView3d(): string | null {
    return this.viewer3dService.currentView3d;
  }

  /**
   * Indica si el minimapa esta habilitado en configuracion y ya inicializado.
   */
  private get isMinimapEnabled(): boolean {
    return this.venueConfigs.minimapConfig().enabled && this.minimap.isInitialized();
  }


  // MAIN INIT METHOD
  /**
   * Inicializa el viewer 2D y el viewer 3D con la configuracion DVM vigente.
   */
  public initViewers(): void {

    this.initMapViewer(); // Inicia el viewer 2D

    this.viewer3dService.initViewer3d();  // Inicia el viewer 3D

  }


  //#region DELEGATED PUBLIC METHODS

  /**
   * Aplica disponibilidad al viewer indicado delegando la resolucion en `ViewerService`.
   *
   * @param type Tipo de nodo DVM al que aplicar la disponibilidad.
   * @param availability Identificadores que deben quedar disponibles.
   * @param viewer Instancia de viewer sobre la que aplicar los cambios.
   */
  public setAvailability(type?: string, availability?: string[], viewer: MapViewerService = this.viewer): void {
    this.viewer2d.setAvailability(type, availability, viewer);
  }

  /**
   * Carga un mapa 2D y sincroniza el minimapa si esta inicializado.
   *
   * @param mapId Identificador del mapa a cargar. Si se omite, usa el `map_id` configurado.
   * @param type Tipo de nodo para una disponibilidad explicita.
   * @param availability Identificadores disponibles para el tipo indicado.
   * @returns Observable emitido por la carga del mapa DVM.
   */
  public loadMap(mapId?: string, type?: string, availability?: string[]): Observable<MapViewerLoadObject> {
    return this.viewer2d.loadMap(mapId, type, availability).pipe(
      tap((): void => {
        if (this.isMinimapEnabled) this.minimapGoTo(mapId ?? this.viewer2d.viewerLoadOptions.map_id!);
      })
    );
  }

  /**
   * Centra el viewer 2D delegando la escala especifica de mapas Main en `ViewerService`.
   *
   * @param target Nodo, nodos, bounding box o posicion de escena de destino.
   * @returns Promesa emitida por la navegacion DVM.
   */
  public goTo(target: Parameters<MapViewerService['goTo']>[0]): Promise<boolean> {
    return this.viewer2d.goTo(target);
  }

  /**
   * Elimina la plantilla de estilos cacheada para que el viewer use estilos base o nuevos estilos.
   */
  public clearStyles(): void {
    this.viewer2d.clearStyles();
  }

  /**
   * Restablece navegacion y mapa activo. Si hay pano activo, lo cierra antes de resetear 2D.
   *
   * @param loadMap Indica si debe recargar el mapa inicial cuando el mapa actual es distinto.
   */
  public resetViewer(loadMap: boolean = true): void {

    // Si la vista es 3D, cerramos el viewer 3D.
    if(this.view() === AppViews.Pano) this.close3dView();

    this.viewer2d.resetViewer(loadMap);

  }

  /**
   * Carga una vista 3D y mueve el minimapa al `viewId` cuando la vista aplica a 3D.
   *
   * @param viewId Identificador de la vista 3D DVM.
   * @returns Observable emitido por el SDK 3D.
   */
  public load3dView(viewId: string): Observable<Viewer3dLoadObject | Error> {
    return this.viewer3dService.load3dView(viewId).pipe(
      tap((): void => {
        if (this.isMinimapEnabled && this.viewDetermination.apply3DView(viewId)) this.minimapGoTo(viewId);
      }),
      catchError((error: Error): Observable<never> => {
        this.viewer2d.resetViewer();
        throw error;
      })
    );
  }

  /**
   * Carga una vista 3D solo cuando el SDK confirma que existe.
   *
   * @param viewId Identificador de la vista 3D DVM.
   * @returns `true` despues de cargar el pano y `false` si se confirma que no existe.
   */
  public load3dViewIfAvailable(viewId: string): Observable<boolean> {
    return this.viewer3dService.has3dView(viewId).pipe(
      switchMap((available: boolean): Observable<boolean> => available
        ? this.load3dView(viewId).pipe(map((): boolean => true))
        : of(false)
      )
    );
  }

  public replaceSelection(target: Parameters<MapViewerService['select']>[0]): void {
    this.viewer.unselectAll();
    this.viewer.select(target);
  }

  public load3dViewOrSelect(viewId: string, enabled = true): Observable<boolean> {
    if (!enabled) {
      this.replaceSelection(viewId);
      return of(false);
    }

    return this.load3dViewIfAvailable(viewId).pipe(
      tap((loaded) => {
        if (!loaded) this.replaceSelection(viewId);
      })
    );
  }

  /**
   * Cierra el pano 3D activo y devuelve el estado de vista al viewer 2D.
   */
  public close3dView(): void {
    this.viewer3dService.close3dView();
  }

  /**
   * Configura el nivel de layer visible en el viewer 2D.
   *
   * @param automaticControlLevel Indica si DVM debe controlar automaticamente el nivel.
   * @param level Nivel de layer que debe quedar activo.
   */
  public setLayers(automaticControlLevel: boolean, level: number): void {
    this.viewer2d.setLayers(automaticControlLevel, level);
  }

  /**
   * Indica si un nodo debe abrir vista 3D segun configuracion y comportamiento GA.
   *
   * @param nodeId Identificador del nodo DVM.
   * @returns `true` si corresponde cargar pano 3D para el nodo.
   */
  public apply3DView(nodeId: string): boolean {
    return this.viewDetermination.apply3DView(nodeId);
  }

  /**
   * Comprueba si una seccion esta configurada para comportarse como General Admission.
   *
   * @param nodeId Identificador del nodo DVM.
   * @returns `true` si la seccion esta incluida en la lista configurada.
   */
  public isGAException(nodeId: string): boolean {
    return this.viewDetermination.isGAException(nodeId);
  }

  /**
   * Indica si un nodo debe seleccionarse en topview en lugar de navegar o abrir 3D.
   *
   * @param nodeId Identificador del nodo DVM.
   * @returns `true` si el nodo debe quedarse seleccionado en topview.
   */
  public selectNodeInTopview(nodeId: string): boolean {
    return this.viewDetermination.selectNodeInTopview(nodeId);
  }

  //#endregion


  //#region PRIVATE ORCHESTRATION METHODS

  /**
   * Inicializa el viewer 2D, espera a que este listo y carga el mapa configurado.
   */
  private initMapViewer(): void {

    this.viewer2d.initMapViewer();

    // Esperamos que encienda y cargamos las load options, el debugger, el mapa y el minimapa
    const viewerReady$: Observable<MapViewerService> = this.viewer.isInitialized()
      ? of(this.viewer)
      : this.viewer.waitInitialize().pipe(first());

    viewerReady$.subscribe((): void => {

      this.addDvmDebugger();

      if (!this.viewer2d.viewerLoadOptions.venue_id || !this.viewer2d.viewerLoadOptions.map_id) return;

      this.loadMap().subscribe({
        next: (): void => {

          if(this.venueConfigs.minimapConfig().enabled) this.initMinimap(); // Cargamos el mapa y si el minimapa está habilitado, lo iniciamos.

          if(this.seatLevelEnabled()) this.setLayers(false, 1) //Set layers according to seatLevelDisplay
        },
        error: (error: unknown): void => console.error('Error loading initial map', error)
      });

    })

  }

  /**
   * Inicializa o reutiliza el minimapa, carga su mapa y aplica flags, disponibilidad y estilos.
   */
  private initMinimap(): void {

    // Si el mapa es tipo main, el minimapa se gestiona via plugin navigable_minimap
    if(this.viewer2d.view() === AppViews.Main) return;

    this.setMinimapLoadOptions();

    const init$: Observable<MapViewerService> = !this.minimap.isInitialized()
      ? from(this.minimap.initialize(this.minimapInitOptions))
      : of(this.minimap);

    init$.pipe(
      switchMap((): Observable<MapViewerLoadObject> => from(this.minimap.loadMap(this.minimapLoadOptions))),
      tap((): void => {
        this.setMinimapFlags();
        this.viewer2d.setAvailability(undefined, undefined, this.minimap);
        this.setMinimapStyles();
        const currentMap: string | null = this.viewer2d.currentMap;
        if (currentMap) this.minimapGoTo(currentMap);
      })
    ).subscribe({
      error: (error: unknown): void => console.error('Error initializing minimap', error)
    });

  }

  /**
   * Recalcula la vista actual a partir del estado 3D y del mapa cargado.
   */
  private setCurrentView(): void {
    this.viewDetermination.setCurrentView(this.is3d());
  }

  //#region MINIMAP METHODS

  /**
   * Centra el minimapa en un mapa o vista concreta y sincroniza la seleccion visual.
   *
   * @param mapId Identificador del mapa o vista que debe enfocarse en el minimapa.
   */
  public minimapGoTo(mapId: string): void {

    if(this.viewer2d.viewerLoadOptions.map_id !== mapId) {

      this.minimap.unselectAll();
      this.minimap.select(mapId);
      this.minimap.goTo(mapId);

    }
    else{

      this.minimap.unselectAll();

      if(this.minimap.limits?.bbox_limits){
        this.minimap.goTo(this.minimap.limits.bbox_limits);
      }

    }

  }

  /**
   * Construye las opciones de carga del minimapa desde la configuracion DVM actual.
   */
  private setMinimapLoadOptions(): void {

    this.minimapLoadOptions.venue_id = this.venueConfigs.viewerConfig().venueId;
    this.minimapLoadOptions.map_id   = this.venueConfigs.minimapConfig().mapId;

  }

  /**
   * Aplica flags al minimapa o restaura los flags por defecto si no se pasan valores.
   *
   * @param flags Flags del SDK DVM que deben sobrescribirse en el minimapa.
   */
  private setMinimapFlags(flags?: MapViewerFlagsAPI): void {

    if(!flags){
      this.setMinimapDefaultFlags();
      return;
    }

    Object.entries(flags).forEach(([key, value]) => {
      if(Object.prototype.hasOwnProperty.call(this.minimap.flags, key)){
          this.minimap.flags[key as keyof MapViewerFlagsAPI] = value;
      }
    });

  }

  /**
   * Define los flags base del minimapa usados por el minisite.
   */
  private setMinimapDefaultFlags(): void {

    const minimap = this.minimap;

    minimap.flags.fixed_aspect_ratio = false;
    minimap.max_selection = 1;

  }

  /**
   * Ajusta los estilos del minimapa para destacar la seleccion de seccion.
   */
  private setMinimapStyles(): void {

    const minimapStyles:    MapViewerStylesLevel = this.minimap.getStyles()[0],
          selectedSection:  MapViewerStylesState = minimapStyles['section']['selected'] as MapViewerStylesState;

    selectedSection.normal.none.fillStyle = '#fff';

    this.minimap.setStyles([minimapStyles], true);

  }

  //#endregion

  /**
   * Expone las instancias DVM en `window` durante desarrollo para depuracion manual.
   */
  private addDvmDebugger(): void {

    if(!isDevMode()) return;

    (window as { dvm?: MapViewerService }).dvm                        = this.viewer;
    (window as { dvm3d?: Viewer3dSdkService }).dvm3d                  = this.viewer3d;
    (window as { dvmMinimap?: MapViewerService }).dvmMinimap          = this.minimap;
    (window as { currentView?: Signal<AppViews | null> }).currentView = this.view;

  }

  //#endregion

}
